import React, { useState, useMemo } from 'react';

const ALL_TICKETS = [
  { id: 'TK-1001', title: 'Login timeout on mobile', priority: 'High', status: 'Open', assignee: 'Alice', created: '2026-01-05', votes: 34 },
  { id: 'TK-1002', title: 'Payment fails for Amex cards', priority: 'Critical', status: 'In Progress', assignee: 'Bob', created: '2026-01-08', votes: 89 },
  { id: 'TK-1003', title: 'Dark mode flicker on Safari', priority: 'Low', status: 'Open', assignee: 'Carol', created: '2026-01-10', votes: 12 },
  { id: 'TK-1004', title: 'Export CSV missing columns', priority: 'Medium', status: 'Resolved', assignee: 'Alice', created: '2026-01-12', votes: 27 },
  { id: 'TK-1005', title: 'Search returns stale results', priority: 'High', status: 'Open', assignee: 'Dave', created: '2026-01-15', votes: 56 },
  { id: 'TK-1006', title: 'Notification emails delayed', priority: 'Medium', status: 'Open', assignee: 'Eve', created: '2026-01-17', votes: 41 },
  { id: 'TK-1007', title: 'API rate limit not enforced', priority: 'Critical', status: 'Open', assignee: 'Bob', created: '2026-01-19', votes: 103 },
  { id: 'TK-1008', title: 'Profile photo upload fails', priority: 'Low', status: 'Resolved', assignee: 'Carol', created: '2026-01-20', votes: 8 },
  { id: 'TK-1009', title: 'Dashboard charts not loading', priority: 'High', status: 'In Progress', assignee: 'Dave', created: '2026-01-22', votes: 67 },
  { id: 'TK-1010', title: 'Password reset link expired', priority: 'High', status: 'Open', assignee: 'Alice', created: '2026-01-25', votes: 45 },
  { id: 'TK-1011', title: '2FA code not sent via SMS', priority: 'Critical', status: 'Open', assignee: 'Eve', created: '2026-01-27', votes: 78 },
  { id: 'TK-1012', title: 'Invoice PDF layout broken', priority: 'Medium', status: 'Open', assignee: 'Frank', created: '2026-01-29', votes: 19 },
  { id: 'TK-1013', title: 'Webhook delivery fails on retry', priority: 'High', status: 'In Progress', assignee: 'Bob', created: '2026-02-01', votes: 53 },
  { id: 'TK-1014', title: 'Audit logs missing user ID', priority: 'Medium', status: 'Resolved', assignee: 'Grace', created: '2026-02-03', votes: 31 },
  { id: 'TK-1015', title: 'Slow query on reports page', priority: 'High', status: 'Open', assignee: 'Dave', created: '2026-02-06', votes: 72 },
  { id: 'TK-1016', title: 'Team invite email broken', priority: 'Critical', status: 'Resolved', assignee: 'Alice', created: '2026-02-08', votes: 44 },
  { id: 'TK-1017', title: 'Timezone mismatch in scheduler', priority: 'Medium', status: 'Open', assignee: 'Carol', created: '2026-02-10', votes: 29 },
  { id: 'TK-1018', title: 'Bulk delete crashes UI', priority: 'High', status: 'Open', assignee: 'Eve', created: '2026-02-13', votes: 61 },
  { id: 'TK-1019', title: 'Search filter resets on paginate', priority: 'Low', status: 'Open', assignee: 'Frank', created: '2026-02-15', votes: 17 },
  { id: 'TK-1020', title: 'SSO redirect loop', priority: 'Critical', status: 'In Progress', assignee: 'Bob', created: '2026-02-17', votes: 95 },
];

const PAGE_SIZE = 7;
const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };

// Task: Filter Status=Open, Priority=High, Sort by Votes descending, Page 1, find top assignee
// Open + High: TK-1001(34), TK-1005(56), TK-1010(45), TK-1015(72), TK-1018(61)
// Sorted by votes desc: TK-1015(72), TK-1018(61), TK-1005(56), TK-1010(45), TK-1001(34)
// Top result: TK-1015 — assignee = Dave
// Q2: How many Open+High tickets total? = 5
// Q3: On page 2 of ALL tickets (no filter) sorted by votes desc — what is ticket ID at position 1?
// All sorted by votes desc: TK-1007(103),TK-1020(95),TK-1002(89),TK-1011(78),TK-1015(72),TK-1009(67),TK-1018(61),
//   TK-1005(56),TK-1013(53),TK-1010(45),TK-1006(41),TK-1016(44->sorted),TK-1001(34)...
// Re-sort: 103,95,89,78,72,67,61,56,53,45,44,41,34,31,29,27,19,17,12,8
// TK-1007,1020,1002,1011,1015,1009,1018,1005,1013,1010,1016,1006,1001,1014,1017,1004,1019,1012,1003,1008
// Page 1 = items 0-6 (TK-1007..TK-1018), Page 2 = items 7-13 => pos 1 on page 2 = TK-1005

const EXPECTED = { topAssignee: 'Dave', openHighCount: '5', page2TopId: 'TK-1005' };

export default function C20_PaginatedSearch({ onComplete }) {
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [answers, setAnswers] = useState({ topAssignee: '', openHighCount: '', page2TopId: '' });
  const [submitted, setSubmitted] = useState(false);
  const [interactions, setInteractions] = useState(0);
  const [errorAttempts, setErrorAttempts] = useState(0);

  const track = () => setInteractions(n => n + 1);

  const filtered = useMemo(() => {
    let d = ALL_TICKETS;
    if (statusFilter !== 'All') d = d.filter(t => t.status === statusFilter);
    if (priorityFilter !== 'All') d = d.filter(t => t.priority === priorityFilter);
    return [...d].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'votes') cmp = a.votes - b.votes;
      else if (sortBy === 'priority') cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      else if (sortBy === 'created') cmp = a.created.localeCompare(b.created);
      else cmp = a.id.localeCompare(b.id);
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [statusFilter, priorityFilter, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const PRIORITY_COLOR = { Critical: 'var(--red)', High: 'var(--gold)', Medium: 'var(--blue)', Low: 'var(--muted)' };
  const STATUS_CLASS = { Open: 'ab-badge-blue', 'In Progress': 'ab-badge-orange', Resolved: 'ab-badge-green' };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
    track();
  };

  const ch = (e) => { setAnswers(a => ({ ...a, [e.target.name]: e.target.value })); track(); };

  const handleSubmit = () => {
    if (!answers.topAssignee.trim() || !answers.openHighCount.trim() || !answers.page2TopId.trim()) {
      setErrorAttempts(n => n + 1); return;
    }
    setSubmitted(true);
    const norm = s => s.trim().toLowerCase();
    const r1 = norm(answers.topAssignee) === norm(EXPECTED.topAssignee);
    const r2 = answers.openHighCount.trim() === EXPECTED.openHighCount;
    const r3 = answers.page2TopId.trim().toUpperCase() === EXPECTED.page2TopId;
    onComplete({
      score: Math.round(([r1, r2, r3].filter(Boolean).length / 3) * 100),
      details: {
        topAssignee: { expected: EXPECTED.topAssignee, got: answers.topAssignee.trim(), pass: r1 },
        openHighCount: { expected: EXPECTED.openHighCount, got: answers.openHighCount.trim(), pass: r2 },
        page2TopId: { expected: EXPECTED.page2TopId, got: answers.page2TopId.trim().toUpperCase(), pass: r3 },
      },
      passed: r1 && r2 && r3,
      interactionCount: interactions,
      errorAttempts,
    });
  };

  const SortHeader = ({ col, label }) => (
    <th onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label} {sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
    </th>
  );

  return (
    <div className="ab-challenge-ui">
      <div className="ab-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ab-filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); track(); }} className="ab-input ab-input-sm" disabled={submitted}>
            {['All', 'Open', 'In Progress', 'Resolved'].map(v => <option key={v}>{v}</option>)}
          </select>
        </div>
        <div className="ab-filter-group">
          <label>Priority:</label>
          <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); track(); }} className="ab-input ab-input-sm" disabled={submitted}>
            {['All', 'Critical', 'High', 'Medium', 'Low'].map(v => <option key={v}>{v}</option>)}
          </select>
        </div>
        <span className="ab-row-count"><strong>{filtered.length}</strong> tickets · Page {safePage}/{totalPages}</span>
      </div>

      <div className="ab-table-wrap">
      <table className="ab-table">
        <thead>
          <tr>
            <SortHeader col="id" label="Ticket ID" />
            <th>Title</th>
            <SortHeader col="priority" label="Priority" />
            <th>Status</th>
            <th>Assignee</th>
            <SortHeader col="created" label="Created" />
            <SortHeader col="votes" label="Votes" />
          </tr>
        </thead>
        <tbody>
          {pageItems.map(t => (
            <tr key={t.id}>
              <td><strong>{t.id}</strong></td>
              <td style={{ fontSize: 12 }}>{t.title}</td>
              <td><span className="ab-diff-chip" style={{ color: PRIORITY_COLOR[t.priority], borderColor: PRIORITY_COLOR[t.priority] }}>{t.priority}</span></td>
              <td><span className={`ab-badge ${STATUS_CLASS[t.status]}`}>{t.status}</span></td>
              <td>{t.assignee}</td>
              <td style={{ fontSize: 11 }}>{t.created}</td>
              <td><strong>{t.votes}</strong></td>
            </tr>
          ))}
          {pageItems.length === 0 && <tr><td colSpan="7" className="ab-empty-row">No tickets match filters.</td></tr>}
        </tbody>
      </table>
      </div>

      <div className="ab-pagination">
        <button className="eval-btn" onClick={() => { setPage(p => Math.max(1, p - 1)); track(); }} disabled={safePage === 1 || submitted}>← Prev</button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button key={i + 1}
            className={`ab-page-btn ${safePage === i + 1 ? 'ab-page-btn-active' : ''}`}
            onClick={() => { setPage(i + 1); track(); }} disabled={submitted}>
            {i + 1}
          </button>
        ))}
        <button className="eval-btn" onClick={() => { setPage(p => Math.min(totalPages, p + 1)); track(); }} disabled={safePage === totalPages || submitted}>Next →</button>
      </div>

      <div className="ab-reasoning-questions">
        <div className="ab-reasoning-q">
          <label className="ab-label">Q1. Filter Open + High priority, sort by Votes descending. Who is the assignee of the TOP result?</label>
          <input name="topAssignee" value={answers.topAssignee} onChange={ch}
            className="ab-input ab-answer-input" placeholder="Assignee name" disabled={submitted} />
        </div>
        <div className="ab-reasoning-q">
          <label className="ab-label">Q2. How many tickets are Open AND High priority in total?</label>
          <input name="openHighCount" value={answers.openHighCount} onChange={ch}
            className="ab-input ab-answer-input" placeholder="Count" disabled={submitted} />
        </div>
        <div className="ab-reasoning-q">
          <label className="ab-label">Q3. Show ALL tickets (no filter), sort by Votes descending. What is the Ticket ID of the FIRST item on Page 2?</label>
          <input name="page2TopId" value={answers.page2TopId} onChange={ch}
            className="ab-input ab-answer-input" placeholder="TK-XXXX" disabled={submitted} />
        </div>
      </div>

      {!submitted
        ? <button className="ab-action-btn" onClick={handleSubmit}>Submit All Answers</button>
        : <div className="ab-submitted-badge">✓ Submitted — Evaluating...</div>
      }
    </div>
  );
}
